import { Drawer } from "@mui/material";
import React, { useEffect, useState } from "react";
import { SidebarMenuItemType } from "../../../services/helpers/types";
import useIsLoading from "../../../services/hooks/useIsLoading";
import classNames from "classnames";
import {
  isThisArrayNotEmpty,
  scrollElementToTop,
  scrollIntoViewIfNeeded,
  scrollToElement,
} from "../../../services/helpers/commonHelpers";
import { CurrentSidebarItemState } from "../../pages/MainPage/MainPage";
import SidebarInnerList from "../SidebarInnerList/SidebarInnerList";
import SidebarTabletView from "../SidebarTabletView/SidebarTabletView";
import { useDispatch } from "react-redux";
import { setIsAiAssistantDrawerOpen } from "../../../redux/reducers/appReducer";

interface SideBarProps {
  items: SidebarMenuItemType[];
  currentSidebarItem: CurrentSidebarItemState;
  isTabletView: boolean;
  isAiAssistantOpen: boolean;
  isDictationOpen: boolean;
  onAiAssistantOpen: () => void;
  onPageSelect: (
    itemID?: number,
    isNewItem?: boolean,
    shouldRefresh?: boolean,
  ) => void;
  onPageCreate: (itemID: number) => void;
  onSettingsOpen: () => void;
  onEditToggle: () => void;
  onDictationOpen: () => void;
}

const Sidebar = (props: SideBarProps) => {
  const {
    items,
    currentSidebarItem,
    isTabletView,
    isAiAssistantOpen,
    isDictationOpen,
    onAiAssistantOpen,
    onPageSelect,
    onSettingsOpen,
    onPageCreate,
    onEditToggle,
    onDictationOpen,
  } = props;
  const { isSidebarLoading } = useIsLoading();
  const dispatch = useDispatch();

  const [sidebarItems, setSidebarItems] = useState<SidebarMenuItemType[]>([]);

  const [compSidebarItem, setCompSidebarItem] =
    useState<CurrentSidebarItemState>(currentSidebarItem);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarFullScreen, setIsSidebarFullScreen] = useState(false);

  useEffect(() => {
    if (currentSidebarItem.isNewItem !== undefined && isTabletView) {
      scrollToElement("dui-sidebar-mid", "dui-sidebar-item-active");
    }

    if (
      currentSidebarItem.item !== undefined &&
      currentSidebarItem.isNewItem !== undefined &&
      currentSidebarItem.item.sidebarItemID !==
        compSidebarItem.item?.sidebarItemID
    ) {
      scrollIntoViewIfNeeded(
        document.getElementById("dui-sidebar-item-active"),
      );
      setCompSidebarItem(currentSidebarItem);
    }
  }, [compSidebarItem, currentSidebarItem, isSidebarFullScreen, isTabletView]);

  useEffect(() => {
    if (!isSidebarFullScreen) {
      scrollToElement("dui-sidebar-mid", "dui-sidebar-item-active");
    }
  }, [isSidebarFullScreen]);

  useEffect(() => {
    setSidebarItems(items);
  }, [items]);

  const handlePageSelect = (itemID?: number, shouldRefresh?: boolean) => {
    scrollElementToTop("dui-report-text-area", "smooth");
    scrollElementToTop("dui-report-text-area-formatted", "smooth");

    dispatch(setIsAiAssistantDrawerOpen(false));

    if (isTabletView) {
      setSidebarItems(items);
      setIsSidebarOpen(false);
      onPageSelect(itemID, false, shouldRefresh);
    } else if (isSidebarFullScreen) {
      setSidebarItems(items);
      setIsSidebarFullScreen(false);
      onPageSelect(itemID, true);
    } else {
      onPageSelect(itemID, false, shouldRefresh);
    }
  };

  const handleSearch = (searchValue?: string) => {
    if (searchValue !== undefined && searchValue !== "") {
      if (isThisArrayNotEmpty(items)) {
        const filteredSidebarItems = items.filter((item) => {
          return item.name.toLowerCase().includes(searchValue.toLowerCase());
        });
        setSidebarItems(filteredSidebarItems);
      }
    } else {
      setSidebarItems(items);
    }
  };

  const handleFullScreenOpen = () => {
    setSidebarItems(items);
    setIsSidebarFullScreen(!isSidebarFullScreen);
  };

  const handleTabletOpen = () => {
    setSidebarItems(items);
    setIsSidebarOpen(false);
  };

  const handleSidebarTabletExpand = () => {
    setSidebarItems(items);
    setIsSidebarOpen(true);
  };

  const className = classNames("dui-sidebar-wrapper", {
    "dui-sidebar-full-screen": isSidebarFullScreen,
  });

  const handleSettingsOpen = () => {
    if (isTabletView) {
      setIsSidebarOpen(false);
    }
    onSettingsOpen();
  };

  const handleEditModeToggle = () => {
    if (isTabletView) {
      setIsSidebarOpen(false);
    }
    onEditToggle();
  };

  const handleAiAssistantOpen = () => {
    setIsSidebarOpen(false);
    onAiAssistantOpen();
  };

  return isTabletView ? (
    <SidebarTabletView
      currentSidebarItem={currentSidebarItem}
      sidebarItems={sidebarItems}
      isSidebarOpen={isSidebarOpen}
      isSidebarFullScreen={isSidebarFullScreen}
      isTabletView={isTabletView}
      isSidebarLoading={isSidebarLoading}
      isAiAssistantOpen={isAiAssistantOpen}
      isDictationOpen={isDictationOpen}
      onAiAssistantOpen={handleAiAssistantOpen}
      onFullScreenOpen={handleFullScreenOpen}
      onTabletOpen={handleTabletOpen}
      onSearch={handleSearch}
      onPageSelect={handlePageSelect}
      onSidebarTabletExpand={handleSidebarTabletExpand}
      onSettingsOpen={handleSettingsOpen}
      onPageCreate={onPageCreate}
      onEditToggle={handleEditModeToggle}
      onDictationOpen={onDictationOpen}
    />
  ) : (
    <div className={className}>
      <Drawer
        anchor="left"
        open
        variant="permanent"
        ModalProps={{
          keepMounted: false,
        }}
        className="dui-sidebar-inner"
      >
        <SidebarInnerList
          currentSidebarItem={currentSidebarItem}
          sidebarItems={sidebarItems}
          isSidebarFullScreen={isSidebarFullScreen}
          isTabletView={isTabletView}
          isAiAssistantOpen={isAiAssistantOpen}
          onAiAssistantOpen={handleAiAssistantOpen}
          onFullScreenOpen={handleFullScreenOpen}
          onTabletOpen={handleTabletOpen}
          onSearch={handleSearch}
          onPageSelect={handlePageSelect}
          onSettingsClick={handleSettingsOpen}
          isSidebarLoading={isSidebarLoading}
          onPageCreate={onPageCreate}
          onEditToggle={handleEditModeToggle}
          onDictationOpen={onDictationOpen}
          isDictationOpen={isDictationOpen}
        />
      </Drawer>
    </div>
  );
};

export default Sidebar;
